import styled from "styled-components";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";

import { STATUS_LABEL, fmtDate, overlapKey } from "pages/Sikring/sikringUtils";
import { Sikringsordre } from "pages/Sikring/types";
import { isDevEnv } from "utils/Utils";

/** Overlapp kan bare bekreftes og overstyres i dev og lokalt. I produksjon stopper overlapp bestillingen. */
export function canOverrideOverlap(): boolean {
	return isDevEnv();
}

interface OverlapWarningProps {
	overlaps: Sikringsordre[];
	/** overlapKey for overlappene brukeren har bekreftet, se WizardState.overlapAcknowledgedFor. */
	acknowledgedFor: string;
	onAcknowledge: (key: string) => void;
}

/**
 * Varsel om eksisterende sikringer på samme omfang i en overlappende periode. Må krysses av før brukeren kan gå
 * videre, og i produksjon kommer brukeren ikke videre i det hele tatt (se canOverrideOverlap).
 */
export function OverlapWarning({ overlaps, acknowledgedFor, onAcknowledge }: Readonly<OverlapWarningProps>) {
	if (overlaps.length === 0) return null;
	const key = overlapKey(overlaps);

	return (
		<Card role="alert">
			<Heading>
				<FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: "8px" }} />
				Perioden overlapper {overlaps.length} eksisterende sikring(er) på samme omfang
			</Heading>
			<List>
				{overlaps.map((o) => (
					<li key={o.id}>
						{fmtDate(o.sikring.start)} – {fmtDate(o.sikring.slutt)} · {STATUS_LABEL[o.status]}
					</li>
				))}
			</List>
			{canOverrideOverlap() ? (
				<CheckboxLabel>
					<input type="checkbox" checked={acknowledgedFor === key} onChange={(e) => onAcknowledge(e.target.checked ? key : "")} />
					Jeg har vurdert overlappet og vil likevel fortsette
				</CheckboxLabel>
			) : (
				<Blocked>Endre periode eller omfang for å gå videre. En sikring kan ikke overlappe en eksisterende sikring.</Blocked>
			)}
		</Card>
	);
}

const Card = styled.div`
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.coral800};
	background: ${colors.coral100};
	padding: 16px 22px;
`;

const Heading = styled.div`
	font-weight: 700;
	color: ${colors.coral};
	margin-bottom: 8px;
`;

const List = styled.ul`
	margin: 0 0 12px;
	padding-left: 20px;
	font-size: 0.875rem;
	line-height: 1.7;
	color: ${colors.grey800};
`;

const Blocked = styled.p`
	margin: 0;
	font-weight: 700;
	font-size: 0.875rem;
`;

const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	font-weight: 700;
	font-size: 0.875rem;
`;
